import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_ENTRY, ProductionEntryOutputStockStatusValues, PRODUCTION_ENTRY_OUTPUT_STOCK_STATUS_LABELS, } from '../types/production-entry.js';
import { USER_DATA } from '../types/user.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionEntry (org/{orgId}/production-entry).
 *
 * Top-level transactional collection. No lifecycle fields. `stepId` is a plain
 * pointer (ProductionOrderStep is a subcollection — not addressable as an FK).
 */
export const productionEntryDescriptor = {
    name: PRODUCTION_ENTRY,
    label: 'Apontamento de produção',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-entry',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 5,
    description: 'Production entry (apontamento) — event of production against an OP (spec 0040 §2.6)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique production entry identifier',
        },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: true,
            description: 'Owning production order (derived from the ticket when input is ticket-only)',
        },
        {
            name: 'ticketId',
            label: 'Talão',
            type: 'string',
            required: false,
            description: 'Optional production ticket; must belong to the OP when set',
        },
        {
            name: 'stepId',
            label: 'Etapa',
            type: 'string',
            required: false,
            description: 'Plain pointer to ProductionOrderStep (subcollection — no FK; resolved with OP context)',
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: false,
            description: 'Denormalized from the step when stepId is set; otherwise free',
        },
        {
            name: 'machineId',
            label: 'Máquina',
            type: 'string',
            required: false,
            description: 'Optional machine (not bound to the OP snapshot)',
        },
        {
            name: 'runId',
            label: 'Execução',
            type: 'string',
            required: false,
            description: 'Optional production run (loose link — D4)',
        },
        {
            name: 'factoryId',
            label: 'Fábrica',
            type: 'string',
            required: false,
            description: 'Optional factory, filled by the cascade when omitted',
        },
        {
            name: 'quantity',
            example: 48.6,
            label: 'Quantidade',
            type: 'number',
            display: 'quantity',
            required: true,
            description: 'Good quantity produced (no scrap — D5)',
        },
        {
            name: 'reportedAt',
            example: '2026-10-22',
            label: 'Apontado em',
            type: 'date',
            display: 'datetime',
            required: true,
            description: 'Report timestamp (default now; backdating allowed)',
        },
        {
            name: 'startedAt',
            example: '2026-10-22T08:00:00.000Z',
            label: 'Início',
            type: 'date',
            display: 'datetime',
            required: false,
            description: 'Optional start of the work interval (D3)',
        },
        {
            name: 'finishedAt',
            example: '2026-10-22T08:45:00.000Z',
            label: 'Fim',
            type: 'date',
            display: 'datetime',
            required: false,
            description: 'Optional end of the work interval (D3)',
        },
        {
            name: 'operatorUserId',
            label: 'Operador',
            type: 'string',
            required: false,
            description: 'Who produced (may differ from inclusionUserId, who typed)',
        },
        {
            name: 'notes',
            example: 'Operador Carlos Lima — turno 1',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        {
            name: 'outputStockStatus',
            label: 'Estoque (PA)',
            type: 'enum',
            required: false,
            enumValues: [...ProductionEntryOutputStockStatusValues],
            enumLabels: PRODUCTION_ENTRY_OUTPUT_STOCK_STATUS_LABELS,
            defaultValue: 'not-applicable',
            description: "Finished-goods stock integration: not-applicable | pending | posted (F4). 'posted' = every planned ledger line exists.",
        },
        {
            name: 'outputStockError',
            label: 'Erro de estoque (PA)',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
            description: 'Last integration failure (reason for pending)',
        },
        {
            name: 'materialsStockStatus',
            label: 'Estoque (insumos)',
            type: 'enum',
            required: false,
            enumValues: [...ProductionEntryOutputStockStatusValues],
            enumLabels: PRODUCTION_ENTRY_OUTPUT_STOCK_STATUS_LABELS,
            defaultValue: 'not-applicable',
            description: "Material backflush summary: posted only when no child line is pending (0180)",
        },
        {
            name: 'materialsStockError',
            label: 'Erro de estoque (insumos)',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        {
            name: 'hasStockMovements',
            label: 'Há movimento de estoque',
            type: 'boolean',
            required: false,
            defaultValue: false,
            description: 'True after any PA or material ledger line posts. Locks quantity/delete even while a side is still pending (0180).',
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção',
        },
        {
            fieldName: 'ticketId',
            targetEntity: 'ProductionTicket',
            relationship: 'many-to-one',
            displayField: 'ticketNumber',
            description: 'Talão',
        },
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Processo',
        },
        {
            fieldName: 'machineId',
            targetEntity: 'Machine',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Máquina',
        },
        {
            fieldName: 'runId',
            targetEntity: 'ProductionRun',
            relationship: 'many-to-one',
            displayField: 'id',
            description: 'Execução (vínculo frouxo)',
        },
        {
            fieldName: 'factoryId',
            targetEntity: 'Factory',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Fábrica',
        },
        {
            fieldName: 'id',
            targetEntity: 'ProductionEntryMaterial',
            relationship: 'one-to-many',
            description: 'Consumo automático de insumos (0180)',
        },
        {
            fieldName: 'operatorUserId',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
            description: 'Operador (quem produziu)',
        },
    ],
};
//# sourceMappingURL=production-entry.descriptor.js.map